import { useCallback, useEffect, useRef, useState } from 'react';

import styles from './ComponentOverlayLayer.module.css';
import { contentfulEntryUrl } from './contentful';
import { getSectionColor } from './sectionColors';

interface OverlayEntry {
  id?: string;
  name: string;
  rect: DOMRect;
  section: string;
}

function unionRects(a: DOMRect, b: DOMRect): DOMRect {
  const left = Math.min(a.left, b.left);
  const top = Math.min(a.top, b.top);
  const right = Math.max(a.right, b.right);
  const bottom = Math.max(a.bottom, b.bottom);
  return new DOMRect(left, top, right - left, bottom - top);
}

// Measure the rendered box of an overlay wrapper. The wrapper is `display:contents` so it never
// affects layout — but that means it generates no box of its own, and in atlas-astro each component
// is *additionally* wrapped in an `<astro-island>`, which Astro also styles `display:contents`.
// `Range.getBoundingClientRect()` does not reliably descend through a `display:contents` member node:
// it happens to work for the single wrapper level in RR7 atlas, but the extra astro-island level in
// atlas-astro yields offset/shrunk rects (NGF-6389). Instead, recurse past every box-less level
// (`display:contents`, not-yet-laid-out, or `display:none` — all report an all-zero rect) and union
// the client rects of the first descendants that actually generate a box.
function getRenderedRect(el: Element): DOMRect | null {
  const rect = el.getBoundingClientRect();
  if (rect.width !== 0 || rect.height !== 0) return rect;

  let union: DOMRect | null = null;
  for (const child of Array.from(el.children)) {
    const childRect = getRenderedRect(child);
    if (!childRect) continue;
    union = union ? unionRects(union, childRect) : childRect;
  }
  return union;
}

function measureOverlays(): OverlayEntry[] {
  const nodes = document.querySelectorAll<HTMLElement>('[data-overlay-name]');
  const entries: OverlayEntry[] = [];

  nodes.forEach(el => {
    const name = el.dataset.overlayName ?? '';
    const section = el.dataset.overlaySection ?? '';
    const id = el.dataset.overlayId || undefined;

    try {
      const rect = getRenderedRect(el);

      // Skip components that produced no measurable box (hidden or not yet rendered)
      if (!rect) return;

      entries.push({ id, name, rect, section });
    } catch {
      // Ignore measurement errors for individual components
    }
  });

  return entries;
}

export function ComponentOverlayLayer() {
  const [mounted, setMounted] = useState(false);
  const [overlays, setOverlays] = useState<OverlayEntry[]>([]);
  const rafRef = useRef<number | null>(null);

  const scheduleUpdate = useCallback(() => {
    if (rafRef.current !== null) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      setOverlays(measureOverlays());
    });
  }, []);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted) return;

    // Initial measurement
    scheduleUpdate();

    // Scroll listener (capture phase, passive)
    window.addEventListener('scroll', scheduleUpdate, { capture: true, passive: true });

    // ResizeObserver on documentElement for viewport/layout changes
    const resizeObserver = new ResizeObserver(scheduleUpdate);
    resizeObserver.observe(document.documentElement);

    // Debounced MutationObserver on the dynamic page wrapper to catch async/lazy hydration
    let mutationDebounce: ReturnType<typeof setTimeout> | null = null;
    const debouncedScheduleUpdate = () => {
      if (mutationDebounce !== null) clearTimeout(mutationDebounce);
      mutationDebounce = setTimeout(scheduleUpdate, 100);
    };

    // Each page template tags its own root with `data-overlay-root` (Dynamic/Landing/SportsHub/
    // SportsGrid in RR7, DynamicPageTemplate.astro in atlas-astro), so the observer attaches on
    // every template — not just the dynamic-page one. Fall back to <body> if a new template forgets
    // the attribute so re-measures still fire, just noisier.
    const mutationObserver = new MutationObserver(debouncedScheduleUpdate);
    const wrapper = document.querySelector<HTMLElement>('[data-overlay-root]') ?? document.body;
    mutationObserver.observe(wrapper, { childList: true, subtree: true, attributes: false });

    return () => {
      window.removeEventListener('scroll', scheduleUpdate, { capture: true });
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      if (mutationDebounce !== null) clearTimeout(mutationDebounce);
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [mounted, scheduleUpdate]);

  // Render nothing during SSR and initial hydration
  if (!mounted) return null;

  const labelOffsets = computeLabelOffsets(overlays);

  return (
    <div aria-hidden="true" className={styles.overlayLayer}>
      {overlays.map((entry, i) => {
        const color = getSectionColor(entry.section);
        const label = entry.section ? `${entry.name} · ${entry.section}` : entry.name;
        const labelTop = labelOffsets[i];

        return (
          <div
            className={styles.componentBox}
            key={`${entry.name}-${i}`}
            style={{
              top: entry.rect.top,
              left: entry.rect.left,
              width: entry.rect.width,
              height: entry.rect.height,
              outline: `2px solid ${color}`,
            }}
          >
            {entry.id ? (
              <a
                className={styles.componentLabelLink}
                href={contentfulEntryUrl(entry.id)}
                rel="noopener noreferrer"
                style={{ backgroundColor: color, top: labelTop }}
                target="_blank"
                title={`Open Contentful entry ${entry.id}`}
              >
                {label}
              </a>
            ) : (
              <span className={styles.componentLabel} style={{ backgroundColor: color, top: labelTop }}>
                {label}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Labels anchor at the top-left of each component box. When two boxes start at nearly the same
// coordinates (e.g. a zero-height wrapper sitting directly above the next component), their labels
// stack on top of each other and the upper one becomes unclickable. Push each label down just
// enough to clear any earlier label sharing the same left edge.
const LABEL_HEIGHT = 20;
const LABEL_COLUMN_TOLERANCE = 4;

function computeLabelOffsets(entries: OverlayEntry[]): number[] {
  const offsets = new Array<number>(entries.length).fill(0);
  for (let i = 0; i < entries.length; i++) {
    const cur = entries[i];
    const curLabelTop = cur.rect.top;
    let offset = 0;
    for (let j = 0; j < i; j++) {
      const prev = entries[j];
      const sameColumn = Math.abs(prev.rect.left - cur.rect.left) < LABEL_COLUMN_TOLERANCE;
      if (!sameColumn) continue;
      const prevLabelBottom = prev.rect.top + offsets[j] + LABEL_HEIGHT;
      const required = prevLabelBottom - curLabelTop;
      if (required > offset) offset = required;
    }
    offsets[i] = Math.max(0, offset);
  }
  return offsets;
}
